import { useRouter } from 'next/router';
import { Me } from '../../../../lib/me';
import { useTranslation } from '../../../../lib/i18n';
import { Avatar, IconButton, ProgressRing } from '../../ui';
import { useAccountSummary } from '../hooks/use-account-summary';
import { useCreditsSummary } from '../hooks/use-credits-summary';
import { RailTooltip } from './RailTooltip';
import styles from './RailAccount.module.css';

// The rail's foot (Figma `Sidebar-Menu` 22535:282583): the avatar and name, opening Settings. Collapsed it is
// the avatar alone. Who it shows lives in useAccountSummary, which the mobile drawer reads too.
export function RailAccount({ me, collapsed = false }: { me: Me | null; collapsed?: boolean }) {
  const router = useRouter();
  const { initials, displayName, email, photoUrl, onPhotoError, settings } = useAccountSummary(me);

  // Reserve the row's height before `me` resolves so the rail doesn't jump.
  if (!initials) return <div className={styles.placeholder} />;

  const label = displayName || email;
  const open = () => {
    settings.onClick();
    void router.push(settings.path);
  };
  const avatar = <Avatar initials={initials} photoUrl={photoUrl} onPhotoError={onPhotoError} size={30} />;

  if (collapsed) {
    return (
      <RailTooltip label={label}>
        <IconButton aria-label={label} onClick={open} className={styles.avatarButton}>
          {avatar}
        </IconButton>
      </RailTooltip>
    );
  }
  return (
    // A whole avatar + name row acting as one trigger: a DS Button would box it in padding and a hover
    // fill of its own (registered with that reason in test/button-spec.test.ts).
    <button type="button" onClick={open} aria-label={label} title={label} className={`orion-nav-item ${styles.account}`}>
      {avatar}
      <span className={styles.name}>{displayName}</span>
    </button>
  );
}

// The collapsed strip's credits mark (Figma 22535:154112): the share of the allowance used, as a ring with
// the percent inside. It opens the rail, where the full card states the numbers and carries the CTA.
export function RailCreditsRing({ onExpand }: { onExpand?: () => void }) {
  const { t } = useTranslation();
  const credits = useCreditsSummary();
  if (!credits) return null;
  const label = `${t('sidebar.credits')} · ${credits.usageLabel}`;
  return (
    <RailTooltip label={credits.usageLabel}>
      <IconButton aria-label={label} onClick={onExpand} className={styles.ringButton}>
        <ProgressRing percent={credits.fraction * 100} arcColor="var(--ds-green-neon-500)" size={30} stroke={3} />
      </IconButton>
    </RailTooltip>
  );
}
